import React, { useState } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    email,
    regex,
    minLength,
    useTranslate,
    Toolbar,
    SaveButton,
    DeleteButton,
    useRedirect,
    useDataProvider,
    useNotify,
    useRefresh,
    useRecordContext,
} from 'react-admin';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const VENDOR_KNOWN_FIELDS = new Set([
    'name', 'code', 'vendorType', 'contactEmail', 'contactPhone',
    'billingInfo', 'settings', 'isActive', 'allowedSites',
    'apiAllowedOperations', 'apiControlLimitPerMinute', 'apiReadLimitPerMinute',
]);

const mapVendorBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const vendorFieldLabelKey = (field: string): string =>
    VENDOR_KNOWN_FIELDS.has(field)
        ? `resources.vendors.fields.${field}`
        : field;
import {
    Box,
    Typography,
    Alert,
    Card,
    CardContent,
    IconButton,
    Chip,
    CircularProgress,
    Autocomplete,
    TextField as MuiTextField,
    Checkbox,
} from '@mui/material';
import {
    Business,
    ContactPhone,
    Settings,
    ArrowBack,
    Info,
    LocationCity,
    People,
    Badge,
    CreditCard,
    VpnKey,
} from '@mui/icons-material';
import { ApiKeysManagement } from './ApiKeysManagement';
import { ApiAccessSection } from './ApiAccessSection';

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            display: 'flex',
            justifyContent: 'space-between',
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
        <DeleteButton
            sx={{
                color: '#D32F2F',
                '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/vendors')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {translate('resources.vendors.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.vendors.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

const SiteAssignment = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const [loading, setLoading] = useState(false);
    const [sites, setSites] = useState<any[]>([]);
    const [assignedSites, setAssignedSites] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);

    React.useEffect(() => {
        if (record?.id) {
            loadSites();
        }
    }, [record?.id]);

    const loadSites = async () => {
        try {
            setLoading(true);
            // Load all sites
            const { data: allSites } = await dataProvider.getList('sites', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'name', order: 'ASC' },
                filter: {},
            });
            setSites(allSites);

            // Load assigned sites for this vendor
            const currentSiteIds = record?.allowedSites || [];
            if (currentSiteIds.length > 0) {
                const assigned = allSites.filter(site =>
                    currentSiteIds.includes(site.id)
                );
                setAssignedSites(assigned);
            } else {
                setAssignedSites([]);
            }
        } catch (error) {
            notify('Error loading sites', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSiteChange = async (event: any, value: any[]) => {
        setAssignedSites(value);
        const siteIds = value.map(site => site.id);

        try {
            setSaving(true);
            await dataProvider.update('vendors', {
                id: record!.id,
                data: { allowedSites: siteIds },
                previousData: record,
            });
            notify('Site assignment updated successfully', { type: 'success' });
            refresh();
        } catch (error) {
            notify('Error updating site assignment', { type: 'error' });
            // Reload sites on error
            loadSites();
        } finally {
            setSaving(false);
        }
    };

    if (!record) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <LocationCity sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.vendors.sections.siteAssignment') || 'Site Assignment'}
                    </Typography>
                </Box>

                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    {translate('resources.vendors.edit.siteAssignmentHelp') ||
                     'Select which sites this vendor can manage RFID cards and users for. Changes are saved automatically.'}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Autocomplete
                        multiple
                        options={sites}
                        value={assignedSites}
                        onChange={handleSiteChange}
                        loading={saving}
                        getOptionLabel={(option) => option.name || ''}
                        renderOption={(props, option, { selected }) => (
                            <Box component="li" {...props}>
                                <Checkbox
                                    checked={selected}
                                    sx={{ mr: 1 }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1">
                                        {option.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {[option.code, option.addressLine1 || option.address, option.city].filter(Boolean).join(' • ')}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.name}
                                    size="small"
                                    {...getTagProps({ index })}
                                    sx={{
                                        backgroundColor: '#E8F5E9',
                                        color: '#1B5E20',
                                        '& .MuiChip-deleteIcon': {
                                            color: '#388E3C',
                                            '&:hover': {
                                                color: '#1B5E20',
                                            },
                                        },
                                    }}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <MuiTextField
                                {...params}
                                label={translate('resources.vendors.fields.allowedSites') || 'Allowed Sites'}
                                placeholder={loading ? 'Loading sites...' : translate('resources.vendors.edit.searchSites') || 'Search and select sites...'}
                                helperText={
                                    saving ? 'Saving...' :
                                    `${assignedSites.length} ${translate('resources.vendors.siteCount') || 'sites'} assigned`
                                }
                            />
                        )}
                        sx={{
                            '& .MuiAutocomplete-inputRoot': {
                                flexWrap: 'wrap',
                                gap: 0.5,
                                padding: '8px 9px',
                            },
                            '& .MuiAutocomplete-input': {
                                minWidth: { xs: '100px !important', sm: '200px !important' },
                            },
                        }}
                        disableCloseOnSelect
                        fullWidth
                        disabled={saving}
                    />
                )}

                {sites.length === 0 && !loading && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {translate('resources.vendors.edit.noSitesAvailable') || 'No sites are available for assignment'}
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

const UsersDisplay = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    const backendUsers = record.backendUsers || [];
    const rfidUsers = record.rfidUsers || [];
    const totalUsers = backendUsers.length + rfidUsers.length;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <People sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.vendors.sections.users') || 'Users'}
                        <Chip
                            label={totalUsers}
                            size="small"
                            sx={{ ml: 2, backgroundColor: '#E8F5E9', color: '#1B5E20' }}
                        />
                    </Typography>
                </Box>

                {/* Backend Users Section */}
                {backendUsers.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <Badge sx={{ mr: 1, fontSize: 20, color: '#1976D2' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {translate('resources.vendors.userTypes.backend') || 'Admin Users'}
                            </Typography>
                            <Chip
                                label={backendUsers.length}
                                size="small"
                                sx={{ ml: 1 }}
                            />
                        </Box>
                        <Box sx={{ pl: 3.5 }}>
                            {backendUsers.map((user: any) => (
                                <Box key={user.userId} sx={{
                                    py: 1,
                                    borderBottom: '1px solid #F0F0F0',
                                    '&:last-child': { borderBottom: 0 }
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {user.displayName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {user.email}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={user.role || 'Admin'}
                                            size="small"
                                            variant="outlined"
                                            color={user.isActive ? 'success' : 'default'}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* RFID Users Section */}
                {rfidUsers.length > 0 && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <CreditCard sx={{ mr: 1, fontSize: 20, color: '#FF9800' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {translate('resources.vendors.userTypes.rfid') || 'RFID Users'}
                            </Typography>
                            <Chip
                                label={rfidUsers.length}
                                size="small"
                                sx={{ ml: 1 }}
                            />
                        </Box>
                        <Box sx={{ pl: 3.5 }}>
                            {rfidUsers.map((user: any) => (
                                <Box key={user.userId} sx={{
                                    py: 1,
                                    borderBottom: '1px solid #F0F0F0',
                                    '&:last-child': { borderBottom: 0 }
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {user.displayName || `${user.firstName} ${user.lastName}`.trim()}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {user.email || user.phone || 'RFID Card Only'}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={user.isActive ? translate('resources.vendors.status.active') || 'Active' : translate('resources.vendors.status.inactive') || 'Inactive'}
                                            size="small"
                                            variant="outlined"
                                            color={user.isActive ? 'success' : 'default'}
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* No Users Message */}
                {totalUsers === 0 && (
                    <Alert severity="info">
                        <Typography variant="body2">
                            {translate('resources.vendors.edit.noUsers') ||
                             'No users assigned to this vendor. Users can be created through the Backend Users or RFID Cards sections.'}
                        </Typography>
                    </Alert>
                )}

                {/* Info Messages */}
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Alert severity="info" icon={<Badge />}>
                        <Typography variant="caption">
                            {translate('resources.vendors.edit.backendUsersInfo') ||
                             'Backend users have admin access to manage RFID cards and view reports for this vendor.'}
                        </Typography>
                    </Alert>
                    <Alert severity="info" icon={<CreditCard />}>
                        <Typography variant="caption">
                            {translate('resources.vendors.edit.rfidUsersInfo') ||
                             'RFID users are employees who use RFID cards for charging. They don\'t have admin access.'}
                        </Typography>
                    </Alert>
                </Box>
            </CardContent>
        </Card>
    );
};

export const VendorsEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});
    const validateName = [required(translate('ra.validation.required'))];
    const validateCode = [
        required(translate('ra.validation.required')),
        minLength(3, translate('ra.validation.minLength', { min: 3 })),
        regex(/^[A-Z0-9_]+$/, translate('resources.vendors.validation.codeFormat') ||
              'Must contain only uppercase letters, numbers, and underscores'),
    ];
    const validateEmail = [email(translate('ra.validation.email'))];

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.vendors.notifications.updated', { type: 'success', messageArgs: { _: 'Vendor updated' } });
                    redirect('list', 'vendors');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapVendorBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(vendorFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            {translate('resources.vendors.edit.infoMessage') ||
                             'Manage vendor information and assign sites they can manage RFID cards and users for.'}
                        </Typography>
                    </Alert>

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Business sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.vendors.sections.basicInfo') || 'Basic Information'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <TextInput
                                    source="id"
                                    disabled
                                    fullWidth
                                    label={translate('resources.vendors.fields.id')}
                                />

                                <TextInput
                                    source="name"
                                    label={translate('resources.vendors.fields.name')}
                                    validate={validateName}
                                    fullWidth
                                    helperText={translate('resources.vendors.edit.nameHelperText') ||
                                              'The official name of the vendor organization'}
                                />

                                <TextInput
                                    source="code"
                                    label={translate('resources.vendors.fields.code')}
                                    validate={validateCode}
                                    fullWidth
                                    disabled
                                    helperText={translate('resources.vendors.edit.codeHelperText') ||
                                              'Unique identifier cannot be changed after creation'}
                                />

                                <SelectInput
                                    source="vendorType"
                                    label={translate('resources.vendors.fields.vendorType') || 'Vendor Type'}
                                    choices={[
                                        { id: 'building_owner', name: translate('resources.vendors.types.building_owner') || 'Building Owner' },
                                        { id: 'facility_manager', name: translate('resources.vendors.types.facility_manager') || 'Facility Manager' },
                                        { id: 'partner', name: translate('resources.vendors.types.partner') || 'Partner' },
                                        { id: 'corporate', name: translate('resources.vendors.types.corporate') || 'Corporate' },
                                    ]}
                                    validate={required()}
                                    fullWidth
                                    helperText={translate('resources.vendors.edit.vendorTypeHelperText') ||
                                              'Select the type of vendor organization'}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Contact Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <ContactPhone sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.vendors.sections.contactInfo') || 'Contact Information'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <TextInput
                                    source="contactEmail"
                                    label={translate('resources.vendors.fields.contactEmail')}
                                    validate={validateEmail}
                                    fullWidth
                                    helperText={translate('resources.vendors.edit.contactEmailHelperText') ||
                                              'Email address for vendor communications'}
                                />

                                <TextInput
                                    source="contactPhone"
                                    label={translate('resources.vendors.fields.contactPhone')}
                                    fullWidth
                                    helperText={translate('resources.vendors.edit.contactPhoneHelperText') ||
                                              'Phone number for urgent matters'}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Site Assignment Section */}
                    <SiteAssignment />

                    {/* Users Section */}
                    <UsersDisplay />

                    {/* API Keys Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <VpnKey sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.vendors.sections.apiKeys') || 'API Keys'}
                                </Typography>
                            </Box>

                            <ApiKeysManagement />
                        </CardContent>
                    </Card>

                    <ApiAccessSection />

                    {/* Settings Section */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.vendors.sections.settings') || 'Settings'}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <BooleanInput
                                    source="isActive"
                                    label={translate('resources.vendors.fields.isActive')}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />

                                <Box sx={{ mt: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Info sx={{ fontSize: 20, mr: 1, color: '#1976D2' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {translate('resources.vendors.edit.billingInfo') || 'Billing Configuration'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.vendors.edit.billingHelperText') ||
                                         'Billing information and settings can be configured here for cost allocation and reporting.'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};